import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject, distinctUntilChanged, takeUntil } from 'rxjs';
import { PayeeDropdownComponent } from '../payee-form-fields/payee-dropdown/payee-dropdown.component';

@Component({
  selector: 'payee-language-selector',
  templateUrl: './payee-language-selector.component.html',
  styleUrls: ['./payee-language-selector.component.scss'],
  imports: [PayeeDropdownComponent, FormsModule, ReactiveFormsModule],
})
export class PayeeLanguageSelectorComponent implements OnInit, OnDestroy {
  private translate = inject(TranslateService);

  @Input() color = 'white';
  @Input() isBorderButton = false;

  private readonly langKey = 'lang' as const;
  private readonly destroy = new Subject<void>();
  readonly languageControl = new FormControl<string | null>(null);

  langs: string[] = [];

  readonly langLabels: Record<string, string> = {
    hu: 'Magyar',
    en: 'English',
    sk: 'Slovenčina',
  };

  readonly langCountryCodes: Record<string, string> = {
    hu: 'hu',
    en: 'gb',
    sk: 'sk',
  };

  get selectedLanguage(): string {
    return this.languageControl.value ?? this.translate.currentLang;
  }

  getFlagUrl(lang: string): string {
    const code = this.langCountryCodes[lang] || lang;
    return `https://flagcdn.com/w80/${code}.png`;
  }

  get currentLangLabel(): string {
    return (
      this.langLabels[this.translate.currentLang] ||
      this.translate.currentLang.toUpperCase()
    );
  }

  getLangLabel(lang: string): string {
    return this.langLabels[lang.toLowerCase()] || lang.toUpperCase();
  }

  ngOnInit(): void {
    this.languageControl.patchValue(this.translate.currentLang);
    this.langs = this.translate.langs;

    this.languageControl.valueChanges
      .pipe(takeUntil(this.destroy), distinctUntilChanged())
      .subscribe((lang) => {
        if (lang) {
          this.translate.use(lang);
          localStorage.setItem(this.langKey, lang);
        }
      });

    this.translate.onLangChange
      .pipe(takeUntil(this.destroy))
      .subscribe((langChangeEvent) => {
        this.languageControl.patchValue(langChangeEvent.lang, {
          emitEvent: false,
        });
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }
}
